import { Math as PhaserMath, Scene } from 'phaser';
import { AreaRolavel } from '../components/AreaRolavel';
import { BotaoArcade } from '../components/BotaoArcade';
import { desenharPaginaLima, desenharVoltar } from '../components/cenario';
import { MolduraPixel } from '../components/MolduraPixel';
import { SCENES } from '../config/constants';
import { ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { estiloDaPosicao, formatarPontos } from '../services/ranking';
import { Sessao } from '../services/Sessao';
import { t } from '../services/textos';

const LINHA = { x: 207, largura: 869, altura: 70, passo: 84 };
const AREA = { x: 150, y: 268, largura: 960, altura: 532 };

/** 5–7. Ranking (Figma, quadro 50): abas Grupo e Individual. */
export class RankingScene extends Scene {
    constructor () {
        super(SCENES.RANKING);
    }

    async create () {
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }
        const fonte = this.registry.get('fonte');

        desenharPaginaLima(this, { titulo: t('ranking.titulo'), rotuloJogador: Sessao.rotulo });
        desenharVoltar(this, 52, 214, () => this.scene.start(SCENES.AGUARDE, {}));

        // RN-RAN-05: só com a visualização habilitada pelo facilitador.
        const estado = await fonte.obterEstadoAguarde();
        if (!this.scene.isActive()) return;
        if (!estado.rankingHabilitado) {
            this.scene.start(SCENES.AGUARDE, {});
            return;
        }

        try {
            this.ranking = await fonte.obterRanking();
        } catch (falha) {
            console.error('[Ranking] não foi possível carregar.', falha);
            if (this.scene.isActive()) {
                this.add.text(640, 460, t('ranking.erro_carregar'), ARCADE_TEXT.aviso)
                    .setOrigin(0.5);
            }
            return;
        }
        if (!this.scene.isActive()) return;

        this.abas = {
            grupos: new BotaoArcade(this, 420.5, 215, {
                rotulo: t('ranking.aba_grupo'), largura: 433, chanfros: { esq: true, dir: false },
                aoClicar: () => this.mostrar('grupos')
            }),
            individual: new BotaoArcade(this, 860.5, 215, {
                rotulo: t('ranking.aba_individual'), largura: 433, chanfros: { esq: false, dir: true },
                aoClicar: () => this.mostrar('individual')
            })
        };

        this.lista = new AreaRolavel(this, { ...AREA, eixo: 'y' });
        // Barra de rolagem: só aparece quando as linhas não cabem na área.
        this.barra = this.add.graphics();
        this.mostrar('grupos');
    }

    update () {
        this.atualizarBarra?.();
    }

    mostrar (aba) {
        Object.entries(this.abas).forEach(([chave, botao]) => botao.setAlpha(chave === aba ? 1 : 0.5));

        const { conteudo } = this.lista;
        conteudo.removeAll(true);
        this.lista.rolarPara(AREA.y);

        const linhas = this.ranking[aba];
        if (linhas.length === 0) {
            conteudo.add(this.add.text(640 - AREA.x, 180, t('ranking.vazio'), ARCADE_TEXT.aviso)
                .setOrigin(0.5));
        }
        linhas.forEach((linha, i) => this.desenharLinha(linha, i + 1));
        this.lista.definirTamanho(7 + linhas.length * LINHA.passo + 16);
        this.desenharBarra(linhas.length);
    }

    /** Trilho fixo à direita da área e cursor proporcional ao que está visível. */
    desenharBarra (quantidade) {
        this.barra.clear();
        this.atualizarBarra = null;

        const total = 7 + quantidade * LINHA.passo + 16;
        const visivel = AREA.altura;
        if (total <= visivel) return;   // tudo cabe: sem barra

        const trilho = { x: AREA.x + AREA.largura + 14, y: AREA.y + 4, altura: visivel - 8 };
        this.barra.fillStyle(ARCADE_HEX.FIO_CAMPO, 1).fillRoundedRect(trilho.x, trilho.y, 6, trilho.altura, 3);
        this.atualizarBarra = () => {
            const rolado = (AREA.y - this.lista.conteudo.y) / (total - visivel);
            const cursor = Math.max(40, trilho.altura * (visivel / total));
            this.barra.fillStyle(ARCADE_HEX.BRANCO, 1)
                .fillRoundedRect(trilho.x, trilho.y + (trilho.altura - cursor) * PhaserMath.Clamp(rolado, 0, 1), 6, cursor, 3);
        };
        this.atualizarBarra();
    }

    /** Posições 1–3 ganham troféu e degradê; as demais, selo numerado. */
    desenharLinha ({ nome, pontos }, posicao) {
        const { conteudo } = this.lista;
        const estilo = estiloDaPosicao(posicao);
        const x = LINHA.x - AREA.x;
        const y = 7 + (posicao - 1) * LINHA.passo;
        const meio = y + LINHA.altura / 2;

        const g = this.add.graphics();
        MolduraPixel.desenhar(g, x, y, LINHA.largura, LINHA.altura, {
            fundo: estilo.degrade ? estilo.degrade[0][1] : ARCADE_HEX.BRANCO, fio: estilo.fio, fioMeio: estilo.fioMeio
        });
        if (estilo.degrade) {
            // Degradê horizontal por trechos, dentro dos chanfros da moldura.
            const inicio = x + 14, largura = LINHA.largura - 28;
            for (let i = 0; i < estilo.degrade.length - 1; i++) {
                const [de, corDe] = estilo.degrade[i];
                const [ate, corAte] = estilo.degrade[i + 1];
                g.fillGradientStyle(corDe, corAte, corDe, corAte, 1);
                g.fillRect(inicio + largura * de, y + 8.2, largura * (ate - de) + 0.5, LINHA.altura - 16.4);
            }
        }
        conteudo.add(g);

        if (estilo.trofeu) {
            conteudo.add(this.add.image(168 - AREA.x, y - 7, estilo.trofeu).setOrigin(0, 0));
        } else {
            conteudo.add(this.add.circle(207.5 - AREA.x, meio, 23.5, ARCADE_HEX.TINTA));
        }
        conteudo.add(this.add.text(
            (estilo.trofeu ? 209.5 : 207.5) - AREA.x, estilo.trofeu ? y + 23 : meio - 1, String(posicao),
            { ...(estilo.trofeu ? { ...ARCADE_TEXT.nomeRanking, fontSize: '19.5px', letterSpacing: -1.95 } : ARCADE_TEXT.nomeRanking), color: estilo.numero }
        ).setOrigin(0.5));

        conteudo.add(this.add.text((estilo.trofeu ? 259 : 250) - AREA.x, meio - 1, nome.toUpperCase(), {
            ...ARCADE_TEXT.nomeRanking, color: estilo.texto
        }).setOrigin(0, 0.5));
        conteudo.add(this.add.text(1052 - AREA.x, meio, formatarPontos(pontos), {
            ...ARCADE_TEXT.pontosRanking, color: estilo.texto
        }).setOrigin(1, 0.5));
    }
}
